import React, { useState, useEffect, useRef,useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import combo from "../assets/Combo-img.png";
import {
  FiShoppingCart,
  FiLoader,
  FiX,
  FiInbox,
  FiPackage,
  FiGift,
  FiSearch,
} from "react-icons/fi";
import { CheckCircle } from "lucide-react";
import SummaryApi from "../common/index";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

/* ================= HELPERS ================= */
const getUserData = () => {
  const data = localStorage.getItem("user");
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

const getUserId = () => {
  const user = getUserData();
  if (!user) return null;
  return user.user_id || user.id || null;
};

const getGuestCart = () =>
  JSON.parse(sessionStorage.getItem("guest_cart") || "[]");

const setGuestCart = (cart) =>
  sessionStorage.setItem("guest_cart", JSON.stringify(cart));

/* ================= CREATE COMBO MODAL ================= */
const CreateComboModal = ({
  isOpen,
  onClose,
  products,
  comboProduct,
  imageBase,
  userId,
  fetchHeaderCount,
  refreshAllData,
}) => {
  const isProduct1Out = products[0]?.is_stock_available === 0;
  const isProduct2Out = products[1]?.is_stock_available === 0;
  const isComboDisabled = isProduct1Out || isProduct2Out;
 const [units, setUnits] = useState([]);
  const [comboName, setComboName] = useState("Combo Pack");
  const [selectedUnit, setSelectedUnit] = useState(null);
  // const [totalLimit, setTotalLimit] = useState("");
  const [qty1, setQty1] = useState("");
  const [qty2, setQty2] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const WEIGHT_PER_PIECE_GRAMS = 9;
  const [isSubscriber, setIsSubscriber] = useState(false);

useEffect(() => {
    const fetchComboUnits = async () => {
      if (!isOpen || !comboProduct?.product_id) return;
      try {
        const res = await fetch(SummaryApi.getProductById.url, {
          method: SummaryApi.getProductById.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product_id: comboProduct.product_id }),
        });
        const result = await res.json();
        
        if (result.status && result.data && result.data.units) {
          // FIX: Access the .units array specifically
          setUnits(result.data.units);
          if (result.data.units.length > 0) {
            setSelectedUnit(result.data.units[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching units:", error);
      }
    };

    if (isOpen) {
      fetchComboUnits();
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      setIsSubscriber(userData?.is_subscriber === 1);
    }
  }, [isOpen, comboProduct]);

   const totalLimit = selectedUnit ? Number(selectedUnit.pack_size) : 0;
   useEffect(() => {
    if (!totalLimit || isComboDisabled) {
      setQty1("");
      setQty2("");
      return;
    }
    const half = Math.floor(totalLimit / 2);
    setQty1((half + (totalLimit % 2)).toString());
    setQty2(half.toString());
  }, [totalLimit, isComboDisabled]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setComboName("Combo Pack");
   
      setQty1("");
      setQty2("");
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      setIsSubscriber(userData?.is_subscriber === 1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!totalLimit || isComboDisabled) {
      setQty1("");
      setQty2("");
      return;
    }
    const newLimit = Number(totalLimit);
    const half = Math.floor(newLimit / 2);
    setQty1((half + (newLimit % 2)).toString());
    setQty2(half.toString());
  }, [totalLimit, isComboDisabled]);

  const parsedQty1 = qty1 === "" ? 0 : Number(qty1);
  const parsedQty2 = qty2 === "" ? 0 : Number(qty2);
  const totalUsed = parsedQty1 + parsedQty2;

  const price2 = products[1]?.selling_price
    ? Number(products[1].selling_price)
    : 0;
  let leafPriceDisplay = 0;
  let flexPriceDisplay = parsedQty2 * price2;

  if (parsedQty1 > 0) {
    leafPriceDisplay = isSubscriber ? parsedQty1 * 12 : parsedQty1 * 15;
  }
  let finalComboPrice = leafPriceDisplay + flexPriceDisplay;

  const increaseQty1 = () => {
    if (!totalLimit) return;
    const limit = Number(totalLimit);
    const newQty1 = parsedQty1 + 1;
    if (newQty1 > limit - 1) return;
    setQty1(newQty1.toString());
    setQty2((limit - newQty1).toString());
  };
  const decreaseQty1 = () => {
    if (!totalLimit) return;
    const limit = Number(totalLimit);
    const newQty1 = parsedQty1 - 1;
    if (newQty1 < 1) return;
    setQty1(newQty1.toString());
    setQty2((limit - newQty1).toString());
  };
  const increaseQty2 = () => {
    if (!totalLimit) return;
    const limit = Number(totalLimit);
    const newQty2 = parsedQty2 + 1;
    if (newQty2 > limit - 1) return;
    setQty2(newQty2.toString());
    setQty1((limit - newQty2).toString());
  };
  const decreaseQty2 = () => {
    if (!totalLimit) return;
    const limit = Number(totalLimit);
    const newQty2 = parsedQty2 - 1;
    if (newQty2 < 1) return;
    setQty2(newQty2.toString());
    setQty1((limit - newQty2).toString());
  };

  const totalWeightGrams = Number(totalLimit) * WEIGHT_PER_PIECE_GRAMS;

  const handleAddToCart = async () => {
    if (isComboDisabled) {
      toast.error(
        "Combo cannot be created as one or more items are out of stock",
      );
      return;
    }
    if (!totalLimit) {
      toast.error("Please select package size");
      return;
    }
    if (totalUsed !== Number(totalLimit)) {
      toast.error(`Total quantity must be ${totalLimit}`);
      return;
    }
    const total = Number(totalLimit);
    if (total >= 2 && (parsedQty1 < 1 || parsedQty2 < 1)) {
      toast.error("Each product in the combo must have at least 1 piece");
      return;
    }
    if (parsedQty1 === 0 && parsedQty2 === 0) {
      toast.error("At least one product must have quantity > 0");
      return;
    }

    setLoading(true);
    try {
      if (!userId) {
        const guestCart = getGuestCart();
        const productParts = [];
        if (parsedQty1 > 0 && products[0]) {
          productParts.push(
            `${parsedQty1} ${products[0].product_title || "Leaf Pads"}`,
          );
        }
        if (parsedQty2 > 0 && products[1]) {
          productParts.push(
            `${parsedQty2} ${products[1].product_title || "Flex Pads"}`,
          );
        }
        const productNameForCart = productParts.join(" + ") || "Custom Combo";
        const comboItem = {
          product_id: comboProduct.product_id, 
          product_unit_id: selectedUnit?.product_unit_id,
          type: "combo",
          product_type: "combo",
          combo_name: comboName.trim() || `Combo ${totalLimit} pcs`,
          product_name: productNameForCart,
          leaf_pads: parsedQty1,
          flex_pads: parsedQty2,
          total_pads: Number(totalLimit),
          price: finalComboPrice,
          total_weight: totalWeightGrams,
          quantity: 1,
          products: [
            parsedQty1 > 0 && {
              product_id: products[0]?.product_id,
              quantity: parsedQty1,
              product_unit_id: products[0]?.units?.[0]?.product_unit_id || null,
              name: products[0]?.product_title || "Leaf Pads",
              price: products[0]?.selling_price || 0,
            },
            parsedQty2 > 0 && {
              product_id: products[1]?.product_id,
              quantity: parsedQty2,
              product_unit_id: products[1]?.units?.[0]?.product_unit_id || null,
              name: products[1]?.product_title || "Flex Pads",
              price: products[1]?.selling_price || 0,
            },
          ].filter(Boolean),
        };
        guestCart.push(comboItem);
        setGuestCart(guestCart);
        fetchHeaderCount();
        toast.success("Combo added to cart (guest mode)");
        onClose();
        return;
      }

      const composition = [];
      if (parsedQty1 > 0)
        composition.push(
          `${parsedQty1} ${products[0]?.product_title || "Leaf Pads"}`,
        );
      if (parsedQty2 > 0)
        composition.push(
          `${parsedQty2} ${products[1]?.product_title || "Flex Pads"}`,
        );
      const productNameForCart = composition.join(" + ") || "Custom Combo";

      if (!comboProduct) {
        toast.error("Combo product not available");
        setLoading(false);
        return;
      }

      const addRes = await fetch(SummaryApi.addToCart.url, {
        method: SummaryApi.addToCart.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart_id: null,
          user_id: userId,
          product_id: comboProduct.product_id,
          product_unit_id: selectedUnit.product_unit_id,
          product_name: productNameForCart,
          product_type: "combo",
          quantity: 1,
          is_active: 1,
          created_date: new Date().toISOString().slice(0, 19).replace("T", " "),
          price: finalComboPrice,
        }),
      });

      const addResult = await addRes.json();
      if (!addResult.status) {
        toast.error(addResult.message || "Failed to add combo to cart");
        setLoading(false);
        return;
      }

      await fetchHeaderCount();
      refreshAllData();
      toast.success("Combo pack added to cart!");
      onClose();
    } catch (error) {
      console.error("Add combo error:", error);
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate("/cart");
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-rose-950/40 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
          className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-rose-100 mt-[5%]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-rose-100 bg-gradient-to-r from-rose-50 to-pink-50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="bg-rose-500 p-2.5 rounded-2xl text-white">
                <FiGift size={20} />
              </div>
              <div>
                <h2 className="text-xl font-black text-rose-950">
                  Create Combo Pack
                </h2>
                {isComboDisabled ? (
                  <p className="text-[10px] text-red-500 font-bold uppercase animate-pulse tracking-wider">
                    Currently Unavailable — Items Out of Stock
                  </p>
                ) : (
                  <p className="text-[10px] text-rose-400 font-semibold tracking-widest uppercase">
                    Each product minimum 1 piece
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 rounded-2xl hover:bg-rose-100 text-rose-400 hover:text-rose-600 transition-colors"
            >
              <FiX size={20} />
            </button>
          </div>

          {/* Combo Name + Total Limit */}
          <div className="px-6 py-4 border-b border-rose-100 bg-rose-50/30 flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
              <input
                type="text"
                value={comboName}
                onChange={(e) => setComboName(e.target.value)}
                placeholder="Combo Pack Name *"
                className="flex-1 px-4 py-2.5 rounded-xl border border-rose-200 focus:outline-none focus:ring-2 focus:ring-rose-300 text-rose-900 bg-white text-sm"
                required
                readOnly
              />
               <select
  // Store the entire unit object as a JSON string to keep track of both ID and Size
  value={selectedUnit ? JSON.stringify(selectedUnit) : ""}
  onChange={(e) => {
    const val = e.target.value;
    if (val) {
      setSelectedUnit(JSON.parse(val));
    } else {
      setSelectedUnit(null);
    }
  }}
  className="px-4 py-2.5 rounded-xl border border-rose-200 focus:ring-2 focus:ring-rose-300 text-rose-900 bg-white font-medium text-sm min-w-[200px]"
>
  <option value="">Select Package Size</option>
  
  {/* Add a safety check with Array.isArray */}
  {Array.isArray(units) && units.map((unit) => (
    <option key={unit.product_unit_id} value={JSON.stringify(unit)}>
      Pack of {unit.pack_size}
    </option>
  ))}
</select>
            </div>
          </div>

          {/* Two Product Slots */}
          <div className="flex-1 px-6 py-5 overflow-y-auto bg-[#FFF0F3]/30">
            {isComboDisabled && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600">
                <FiPackage size={18} />
                <p className="text-xs font-bold">
                  Sorry! You cannot build a combo right now because
                  {isProduct1Out && isProduct2Out
                    ? " both products are "
                    : isProduct1Out
                      ? ` ${products[0].product_title} is `
                      : ` ${products[1].product_title} is `}
                  out of stock.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {/* ── Product 1 ── */}
              <div className="bg-white rounded-[1.75rem] border border-rose-100 p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden flex flex-col">
                {isProduct1Out && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-[1.75rem]">
                    <span className="bg-red-500 text-white px-4 py-2 rounded-xl font-black text-xs shadow-lg tracking-wide uppercase">
                      Out of Stock
                    </span>
                  </div>
                )}
                {products[0] ? (
                  <>
                    {/* Image */}
                    <div className="w-full aspect-square max-h-[180px] mb-4 bg-rose-50/50 rounded-xl flex items-center justify-center p-4">
                      <img
                        src={
                          products[0].product_image1
                            ? `${imageBase}/${products[0].product_image1}`
                            : "https://via.placeholder.com/200"
                        }
                        alt={products[0].product_title}
                        className="w-full h-full object-contain drop-shadow-md transform hover:scale-105 transition-transform"
                      />
                    </div>

                    {/* Name + Price — reduced sizes */}
                    <div className="text-center mb-4">
                      <h3 className="text-sm font-bold text-rose-950 line-clamp-2 leading-snug mb-1">
                        {products[0].product_title}
                      </h3>
                      <p className="text-sm font-bold text-rose-500">
                        ₹{products[0].selling_price}
                        <span className="text-xs font-medium text-rose-400">
                          {" "}
                          / pad
                        </span>
                      </p>
                    </div>

                    {/* Quantity Selector — compact pill design */}
                    <div className="mt-auto">
                      <div className="flex items-center justify-between bg-rose-50 rounded-2xl border border-rose-100 overflow-hidden mb-3">
                        <button
                          onClick={decreaseQty1}
                          disabled={!totalLimit || parsedQty1 <= 1}
                          className="w-10 h-10 flex items-center justify-center text-rose-600 text-xl font-bold hover:bg-rose-100 disabled:opacity-30 transition-colors flex-shrink-0"
                        >
                          −
                        </button>
                        <div className="flex-1 flex flex-col items-center py-1">
                          <span className="text-2xl font-black text-rose-950 leading-none">
                            {qty1 || "0"}
                          </span>
                          <span className="text-[9px] font-bold text-rose-400 uppercase tracking-wider">
                            pcs
                          </span>
                        </div>
                        <button
                          onClick={increaseQty1}
                          disabled={
                            !totalLimit ||
                            totalUsed >= Number(totalLimit) ||
                            isProduct1Out
                          }
                          className="w-10 h-10 flex items-center justify-center text-rose-600 text-xl font-bold hover:bg-rose-100 disabled:opacity-30 transition-colors flex-shrink-0"
                        >
                          +
                        </button>
                      </div>

                      {/* Subtotal */}
                      <div className="text-center min-h-[2.5rem] flex flex-col items-center justify-center">
                        {isSubscriber && parsedQty1 > 0 ? (
                          <>
                            <p className="text-sm font-black text-rose-600">
                              Subtotal: ₹{(parsedQty1 * 12).toFixed(0)}
                            </p>
                            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100 mt-1">
                              Subscriber: ₹12/pad
                            </span>
                          </>
                        ) : parsedQty1 > 0 ? (
                          <p className="text-sm font-black text-rose-600">
                            Subtotal: ₹{(parsedQty1 * 15).toFixed(0)}
                          </p>
                        ) : (
                          <p className="text-xs text-rose-300 font-medium">
                            Select quantity
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-16 text-rose-400 flex flex-col items-center justify-center h-full">
                    <FiPackage size={40} className="mb-3 opacity-50" />
                    <p className="text-sm font-medium">No product available</p>
                  </div>
                )}
              </div>

              {/* ── Product 2 ── */}
              <div className="bg-white rounded-[1.75rem] border border-rose-100 p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden flex flex-col">
                {isProduct2Out && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-[1.75rem]">
                    <span className="bg-red-500 text-white px-4 py-2 rounded-xl font-black text-xs shadow-lg tracking-wide uppercase">
                      Out of Stock
                    </span>
                  </div>
                )}
                {products[1] ? (
                  <>
                    <div className="w-full aspect-square max-h-[180px] mb-4 bg-rose-50/50 rounded-xl flex items-center justify-center p-4">
                      <img
                        src={
                          products[1].product_image1
                            ? `${imageBase}/${products[1].product_image1}`
                            : "https://via.placeholder.com/200"
                        }
                        alt={products[1].product_title}
                        className="w-full h-full object-contain drop-shadow-md transform hover:scale-105 transition-transform"
                      />
                    </div>

                    <div className="text-center mb-4">
                      <h3 className="text-sm font-bold text-rose-950 line-clamp-2 leading-snug mb-1">
                        {products[1].product_title}
                      </h3>
                      <p className="text-sm font-bold text-rose-500">
                        ₹{products[1].selling_price}
                        <span className="text-xs font-medium text-rose-400">
                          {" "}
                          / pad
                        </span>
                      </p>
                    </div>

                    <div className="mt-auto">
                      <div className="flex items-center justify-between bg-rose-50 rounded-2xl border border-rose-100 overflow-hidden mb-3">
                        <button
                          onClick={decreaseQty2}
                          disabled={!totalLimit || parsedQty2 <= 1}
                          className="w-10 h-10 flex items-center justify-center text-rose-600 text-xl font-bold hover:bg-rose-100 disabled:opacity-30 transition-colors flex-shrink-0"
                        >
                          −
                        </button>
                        <div className="flex-1 flex flex-col items-center py-1">
                          <span className="text-2xl font-black text-rose-950 leading-none">
                            {qty2 || "0"}
                          </span>
                          <span className="text-[9px] font-bold text-rose-400 uppercase tracking-wider">
                            pcs
                          </span>
                        </div>
                        <button
                          onClick={increaseQty2}
                          disabled={
                            !totalLimit || totalUsed >= Number(totalLimit)
                          }
                          className="w-10 h-10 flex items-center justify-center text-rose-600 text-xl font-bold hover:bg-rose-100 disabled:opacity-30 transition-colors flex-shrink-0"
                        >
                          +
                        </button>
                      </div>

                      <div className="text-center min-h-[2.5rem] flex flex-col items-center justify-center">
                        {parsedQty2 > 0 ? (
                          <p className="text-sm font-black text-rose-600">
                            Subtotal: ₹{(parsedQty2 * price2).toFixed(0)}
                          </p>
                        ) : (
                          <p className="text-xs text-rose-300 font-medium">
                            Select quantity
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-16 text-rose-400 flex flex-col items-center justify-center h-full">
                    <FiPackage size={40} className="mb-3 opacity-50" />
                    <p className="text-sm font-medium">No product available</p>
                  </div>
                )}
              </div>
            </div>

            {totalLimit && totalUsed !== Number(totalLimit) && (
              <p className="text-center text-amber-600 font-medium mt-6 text-sm bg-amber-50 py-2.5 rounded-xl border border-amber-100 max-w-xl mx-auto">
                Total: {totalUsed} / {totalLimit} pcs — adjust quantities to
                match exactly
              </p>
            )}

            {totalLimit && Number(totalLimit) >= 2 && (
              <p className="text-center text-[10px] font-bold text-rose-500 mt-3 opacity-70 uppercase tracking-widest">
                * Each product must have minimum 1 piece
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-5 border-t border-rose-100 bg-gradient-to-r from-rose-50 to-pink-50 flex-shrink-0">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-5">
              <div className="text-center sm:text-left">
                <p className="text-xs text-rose-500 font-medium">
                  Total Amount
                </p>
                <p
                  className={`text-3xl font-black ${isComboDisabled ? "text-gray-400" : "text-rose-950"}`}
                >
                  ₹{finalComboPrice.toFixed(0)}
                </p>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={handleAddToCart}
                  disabled={
                    loading ||
                    isComboDisabled ||
                    !totalLimit ||
                    totalUsed !== Number(totalLimit)
                  }
                  className="flex-1 sm:flex-none px-7 py-3 rounded-2xl bg-white border-2 border-rose-500 text-rose-600 font-bold text-sm hover:bg-rose-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
                >
                  Add to Cart
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={
                    loading ||
                    isComboDisabled ||
                    !totalLimit ||
                    totalUsed !== Number(totalLimit)
                  }
                  className="flex-1 sm:flex-none px-7 py-3 rounded-2xl bg-rose-500 text-white font-bold text-sm hover:bg-rose-600 shadow-md shadow-rose-500/30 disabled:opacity-50 disabled:shadow-none disabled:bg-gray-400 transition-all"
                >
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ================= MAIN PRODUCTS COMPONENT ================= */
const Products = () => {
  const [products, setProducts] = useState([]);
  const [comboProduct, setComboProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartMap, setCartMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("default");
  const [isComboModalOpen, setIsComboModalOpen] = useState(false);
  const { fetchCartCount } = useCart();
  const userId = getUserId();
  const userData = getUserData();
  const IMAGE_BASE_URL = SummaryApi?.productImage?.url
    ? SummaryApi.productImage.url.replace("/api/uploads", "")
    : "http://192.168.1.254:3001";

  const refreshCartData = async () => {
    const map = {};
    if (userId) {
      try {
        const res = await fetch(SummaryApi.getCartById.url, {
          method: SummaryApi.getCartById.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const result = await res.json();
        if (result.status && result.data) {
          result.data.forEach((item) => {
            const key = `${item.product_id}_${item.product_unit_id || "no-unit"}`;
            map[key] = {
              qty: item.quantity,
              cart_id: item.cart_id,
              product_unit_id: item.product_unit_id,
            };
          });
        }
      } catch (err) {
        console.error("Cart fetch error", err);
      }
    } else {
      getGuestCart().forEach((item) => {
        if (item.type === "combo") {
          if (item.leaf_pads > 0 && products[0]?.product_id) {
            const key = `${products[0].product_id}_${products[0]?.units?.[0]?.product_unit_id || ""}`;
            if (!map[key]) map[key] = { qty: 0 };
            map[key].qty += item.leaf_pads;
          }
          if (item.flex_pads > 0 && products[1]?.product_id) {
            const key = `${products[1].product_id}_${products[1]?.units?.[0]?.product_unit_id || ""}`;
            if (!map[key]) map[key] = { qty: 0 };
            map[key].qty += item.flex_pads;
          }
        } else {
          const key = `${item.product_id}_${item.product_unit_id || ""}`;
          map[key] = {
            qty: item.quantity,
            product_unit_id: item.product_unit_id,
          };
        }
      });
    }
    setCartMap(map);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchProducts();
    refreshCartData();
  }, [userId]);

  const fetchProducts = async () => {
    try {
      const res = await fetch(SummaryApi.getActiveProduct.url);
      const result = await res.json();
      if (result.status) {
        const nonComboProducts = result.data.filter(
          (p) => String(p.product_type).toLowerCase() !== "combo",
        );
        const comboProd = result.data.find(
          (p) => String(p.product_type).toLowerCase() === "combo",
        );
        setProducts(nonComboProducts);
        setComboProduct(comboProd || null);
      }
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const displayedProducts = (() => {
    let list = [...products];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter((p) => p.product_title?.toLowerCase().includes(term));
    }
    if (sortOption === "price-low")
      list.sort((a, b) => Number(a.selling_price) - Number(b.selling_price));
    else if (sortOption === "price-high")
      list.sort((a, b) => Number(b.selling_price) - Number(a.selling_price));
    return list;
  })();

  const showToastOnce = (product) => {
    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white shadow-2xl rounded-2xl flex border-b-4 border-rose-500"
        >
          <div className="flex-1 p-4 flex gap-3">
            <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-500">
              <CheckCircle size={26} />
            </div>
            <div>
              <p className="text-sm font-bold text-rose-950">
                Added to your cart!
              </p>
              <p className="text-xs text-rose-900/50">
                {product.product_title}
              </p>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-4 font-bold text-rose-600"
          >
            Close
          </button>
        </motion.div>
      ),
      { duration: 1000, position: "top-center" },
    );
  };

  return (
    <div className="min-h-screen bg-[#FFF0F3] p-6 pb-20">
      {/* ===== YOUR CARE COLLECTION ===== */}
      <div className="text-center mb-10 pt-10">
        <h1 className="text-4xl font-black text-rose-950 uppercase mb-2">
          Your Care Collection
        </h1>
        <p className="text-rose-900/40 text-[10px] tracking-[0.3em] font-bold uppercase">
          Comfort delivered to your doorstep
        </p>
      </div>

      <div className="max-w-6xl mx-auto mb-10 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative w-full sm:max-w-md">
          <FiSearch
            className="absolute left-4 top-1/2 -translate-y-1/2 text-rose-400"
            size={20}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-12 pr-10 py-3 rounded-2xl border border-rose-200 focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white/80 backdrop-blur-sm text-rose-900"
          />
        </div>
        <select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          className="w-full sm:w-56 px-4 py-3 rounded-2xl border border-rose-200 bg-white focus:outline-none focus:ring-2 focus:ring-rose-300 text-rose-900"
        >
          <option value="default">Sort by: Featured</option>
          <option value="price-low">Price: Low to High</option>
          <option value="price-high">Price: High to Low</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <FiLoader className="animate-spin text-rose-500" size={40} />
        </div>
      ) : displayedProducts.length > 0 ? (
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {displayedProducts.map((p) => (
            <ProductCard
              key={p.product_id}
              product={p}
              imageBase={IMAGE_BASE_URL}
              isSubscriber={userData?.is_subscriber === 1}
              initialQty={
                cartMap[
                  `${p.product_id}_${p.units?.[0]?.product_unit_id || ""}`
                ]?.qty || 0
              }
              cartIdFromDb={
                cartMap[
                  `${p.product_id}_${p.units?.[0]?.product_unit_id || ""}`
                ]?.cart_id
              }
              userId={userId}
              onFirstAdd={showToastOnce}
              fetchHeaderCount={fetchCartCount}
              refreshAllData={refreshCartData}
            />
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto text-center py-20 bg-white/50 backdrop-blur-sm rounded-[3rem] border border-rose-100 shadow-xl shadow-rose-900/5"
        >
          <div className="flex justify-center mb-6">
            <div className="bg-rose-100 p-6 rounded-full text-rose-400">
              <FiInbox size={48} />
            </div>
          </div>
          <h3 className="text-2xl font-black text-rose-950 mb-2">
            No Products Found
          </h3>
          <p className="text-rose-900/60 px-6">
            {searchTerm
              ? `We couldn't find any products matching "${searchTerm}". Try a different search term.`
              : "Our collection is currently empty. Please check back later!"}
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="mt-6 text-rose-500 font-bold hover:underline"
            >
              Clear search
            </button>
          )}
        </motion.div>
      )}

      {/* ===== COMBO COLLECTION SECTION ===== */}
      {/* ===== COMBO COLLECTION SECTION ===== */}
      <div className="max-w-7xl mx-auto mt-24">
        {/* <div className="text-center mb-10">
    <div className="inline-flex items-center gap-2 bg-rose-100 text-rose-500 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-4">
      <FiGift size={14} /> Best Value
    </div>
    <h2 className="text-4xl font-black text-rose-950 uppercase mb-2">
      Combo Collection
    </h2>
    <p className="text-rose-900/40 text-[10px] tracking-[0.3em] font-bold uppercase">
      Mix & match your favourites — Save more together
    </p>
  </div> */}

        {/* Displaying Combo as a Card Grid */}
        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 w-full">
            {comboProduct && (
              <ComboCard
                comboProduct={comboProduct}
                 imageBase={IMAGE_BASE_URL} // <--- ADD THIS LINE
                onClick={() => setIsComboModalOpen(true)}
              />
            )}
          </div>
        </div>
      </div>

      <CreateComboModal
        isOpen={isComboModalOpen}
        onClose={() => setIsComboModalOpen(false)}
        products={products}
        comboProduct={comboProduct}
        imageBase={IMAGE_BASE_URL}
        userId={userId}
        fetchHeaderCount={fetchCartCount}
        refreshAllData={refreshCartData}
      />
    </div>
  );
};

/* ================= PRODUCT CARD ================= */
const ProductCard = ({
  product,
  imageBase,
  initialQty,
  cartIdFromDb,
  userId,
  onFirstAdd,
  fetchHeaderCount,
  refreshAllData,
  isSubscriber,
}) => {
  const isOutOfStock = product.is_stock_available === 0;
  const [selectedUnit, setSelectedUnit] = useState(product.units?.[0] || null);
  const [qty, setQty] = useState(initialQty);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    setQty(initialQty);
  }, [initialQty]);
  const displayDiscount = useMemo(() => {
    const actual = parseFloat(product.actual_price) || 0;
    const selling = parseFloat(product.selling_price) || 0;
    const dbDiscount = parseInt(product.discount) || 0;

    const isLeaf = product.product_title?.toLowerCase().includes("leaf") ||
                   product.product_title?.toLowerCase().includes("sakhi");

    if (isSubscriber && isLeaf) {
      const subPrice = 12;
      // If we have an actual price (MRP), calculate total discount from MRP to 12
      if (actual > subPrice) {
        return Math.round(((actual - subPrice) / actual) * 100);
      } 
      // If actual price isn't set, calculate the 20% benefit from the standard 15 price
      return Math.max(dbDiscount, 20); 
    }

    return dbDiscount;
  }, [isSubscriber, product, isOutOfStock]);
  const isLeaf =
    product.product_title?.toLowerCase().includes("leaf") ||
    product.product_title?.toLowerCase().includes("sakhi");

  const syncCartWithServer = async (finalQty) => {
    if (!userId) {
      const cart = getGuestCart();
      const index = cart.findIndex(
        (i) =>
          i.type !== "combo" &&
          String(i.product_id) === String(product.product_id) &&
          String(i.product_unit_id) === String(selectedUnit?.product_unit_id),
      );
      if (finalQty === 0) {
        if (index > -1) cart.splice(index, 1);
      } else {
        if (index > -1) {
          cart[index].quantity = finalQty;
        } else {
          cart.push({
            type: "product",
            product_id: product.product_id,
            product_unit_id: selectedUnit?.product_unit_id,
            quantity: finalQty,
          });
        }
      }
      setGuestCart(cart);
      fetchHeaderCount();
      return;
    }

    try {
      let res;
      if (finalQty === 0 && cartIdFromDb) {
        res = await fetch(SummaryApi.cartDelete.url, {
          method: SummaryApi.cartDelete.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_id: cartIdFromDb }),
        });
      } else if (initialQty === 0 && finalQty > 0) {
        res = await fetch(SummaryApi.addToCart.url, {
          method: SummaryApi.addToCart.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cart_id: null,
            user_id: userId,
            product_id: product.product_id,
            product_unit_id: selectedUnit?.product_unit_id,
            quantity: finalQty,
            is_active: 1,
            created_date: new Date()
              .toISOString()
              .slice(0, 19)
              .replace("T", " "),
          }),
        });
      } else if (finalQty > 0 && cartIdFromDb) {
        res = await fetch(SummaryApi.cartUpdate.url, {
          method: SummaryApi.cartUpdate.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cart_id: cartIdFromDb,
            user_id: userId,
            product_id: product.product_id,
            product_unit_id: selectedUnit?.product_unit_id,
            quantity: finalQty,
          }),
        });
      }

      if (res?.ok) {
        await fetchHeaderCount();
        if (initialQty === 0 || finalQty === 0) {
          refreshAllData();
        }
      }
    } catch (err) {
      console.error("Cart sync error:", err);
      setQty(initialQty);
      toast.error("Failed to update cart");
    }
  };

  const handleQtyChange = (delta) => {
    const newQty = Math.max(0, qty + delta);
    setQty(newQty);
    if (qty === 0 && delta > 0) onFirstAdd(product);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => syncCartWithServer(newQty), 500);
  };
    const effectiveDiscount = useMemo(() => {
    if (isSubscriber && isLeaf) {
      // Logic: (15 - 12) / 15 * 100 = 20%
      // If the product already has a higher discount, we show that, 
      // otherwise we show the 20% subscriber benefit.
      return Math.max(product.discount || 0, 20);
    }
    return product.discount || 0;
  }, [isSubscriber, isLeaf, product.discount]);

  const imageUrl = product.product_image1
    ? `${imageBase}/${product.product_image1}`
    : "https://via.placeholder.com/400";

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white rounded-[2rem] overflow-hidden shadow-xl shadow-rose-900/5 flex flex-col h-full border border-rose-100/50 transition-all cursor-pointer"
      onClick={() => {
        const encodedId = window.btoa(product.product_id.toString());
        navigate(`/product/${encodedId}`);
      }}
    >
      <div
        className="relative aspect-square overflow-hidden bg-rose-50/50 cursor-pointer flex items-center justify-center"
        onClick={() => {
          const encodedId = window.btoa(product.product_id.toString());
          navigate(`/product/${encodedId}`);
        }}
      >
        {isOutOfStock && (
          <div className="absolute top-4 left-4 z-10 bg-red-600 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg">
            OUT OF STOCK
          </div>
        )}
          {!isOutOfStock && displayDiscount > 0 && (
          <div className="absolute top-4 right-4 z-20 flex flex-col items-end gap-1">
            <div className="bg-[#d8315b] text-white text-[11px] font-black px-3 py-1 rounded-full shadow-lg flex items-center gap-1 animate-bounce">
              <FiGift size={12} />
              {displayDiscount}% OFF
            </div>
            {isSubscriber && isLeaf && (
              <span className="text-[8px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold shadow-sm">
                SUBSCRIBER SPECIAL
              </span>
            )}
          </div>
        )}
        <img
          src={imageUrl}
          alt={product.product_title}
          className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
        />
        {isOutOfStock && (
          <div className="absolute inset-0 bg-white/20 backdrop-blur-[1px] flex items-center justify-center">
            <span className="bg-white/90 text-red-600 px-4 py-2 rounded-xl font-black text-sm border border-red-100">
              Currently Unavailable
            </span>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex justify-between">
          <h2 className="text-lg font-black text-rose-950 line-clamp-1 mb-3">
            {product.product_title}
          </h2>
        </div>
        <h2 className="text-sm font-black text-rose-500 mb-1 flex">
          <p className="pr-1 text-rose-150">Size:</p> {product.product_size}
        </h2>
        {isOutOfStock ? (
          <p className="text-xs font-bold text-red-500 mb-4 italic">
            This item is out of stock
          </p>
        ) : (
          <p className="text-xs font-bold text-rose-400 mb-4">
            {product.subtitle || "Premium Quality"}
          </p>
        )}
        <div className="mt-auto flex flex-col">
          <p className="text-[10px] font-bold text-rose-300 uppercase tracking-widest">
            Price
          </p>
          <div className="flex flex-wrap items-baseline gap-2">
            {isSubscriber && isLeaf ? (
              <>
                <span className="text-lg font-bold line-through text-rose-300">
                  ₹15.00
                </span>
                <span className="text-2xl font-black text-rose-950">
                  ₹12.00
                </span>
                <div className="w-full mt-1">
                  <span className="bg-emerald-100 text-emerald-600 text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">
                    Subscriber Benefit (₹12/Pad)
                  </span>
                </div>
              </>
            ) : (
              <>
                <span className="text-sm font-bold line-through text-rose-300">
                  ₹{product.actual_price || "---"}
                </span>
                <span className="text-2xl font-black text-rose-950">
                  ₹{product.selling_price}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ComboCard = ({ comboProduct,imageBase, onClick }) => {
   const comboImageUrl = comboProduct.product_image1 
    ? `${imageBase}/${comboProduct.product_image1}` 
    : combo; // Fallback to static combo image if API image is missing

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="group bg-white rounded-[2rem] overflow-hidden shadow-xl shadow-rose-900/5 flex flex-col h-full border border-rose-100/50 transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-rose-100 to-pink-50 flex items-center justify-center">
        {/* Best Value Badge */}
        <div className="absolute top-4 left-4 z-10 bg-[#d8315b] text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg">
          BEST VALUE
        </div>

        {/* Discount/Gift Badge */}
        <div className="absolute top-4 right-4 z-10 bg-white text-rose-500 text-[11px] font-black px-3 py-1 rounded-full shadow-md flex items-center gap-1">
          <FiGift size={12} />
          CUSTOMIZABLE
        </div>

      <img
          src={comboImageUrl}
          alt={comboProduct.product_title || "Combo Pack"}
          className="w-4/5 h-4/5 object-contain transition-transform duration-500 group-hover:scale-110 drop-shadow-2xl"
        />

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-rose-950/0 group-hover:bg-rose-950/5 transition-colors duration-300" />
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex justify-between items-start gap-2">
          <h2 className="text-lg font-black text-rose-950 line-clamp-2 mb-3 flex-1 uppercase">
            {comboProduct.product_title || "Custom Combo Pack"}
          </h2>
          <span className="text-[10px] font-black bg-rose-100 text-rose-500 px-2 py-1 rounded-lg uppercase">
            Mix
          </span>
        </div>

        <p className="text-xs font-bold text-rose-400 mb-4">
         {comboProduct.subtitle || "Choose your own quantity of Leaf & Flex pads"}
        </p>

        <div className="mt-auto flex flex-col">
          {/* <p className="text-[10px] font-bold text-rose-300 uppercase tracking-widest">
            Pricing
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-rose-950">
              Dynamic
            </span>
            <span className="text-xs font-bold text-rose-400">
               Based on selection
            </span>
          </div> */}

          <button className="mt-4 w-full py-3 rounded-xl bg-rose-500 text-white font-bold text-sm group-hover:bg-rose-600 transition-colors shadow-lg shadow-rose-200">
            Create Your Pack
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default Products;
